import { Link } from '@tanstack/react-router'
import { CheckCircle, ChevronRight, Circle, Rocket } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import { Progress } from '@/shared/components/ui/progress'

interface ChecklistItem {
  id: string
  title: string
  description: string
  completed: boolean
  url?: string
}

export function GettingStarted() {
  // In a real implementation, this would come from a service or store
  const checklistItems: ChecklistItem[] = [
    {
      id: 'agent',
      title: 'Create your first agent',
      description: 'Set up an agent to start monetizing AI capabilities',
      completed: false,
      url: '/agents',
    },
    {
      id: 'plan',
      title: 'Design a pricing plan',
      description: 'Define how customers will be charged for agent usage',
      completed: false,
      url: '/catalog/plans/create',
    },
    {
      id: 'customer',
      title: 'Add a customer',
      description: 'Onboard your first customer to start generating revenue',
      completed: false,
      url: '/customers/create',
    },
    {
      id: 'api-key',
      title: 'Generate API keys',
      description: 'Set up authentication for API access',
      completed: false,
      url: '/developers/api-keys',
    },
    {
      id: 'send-billable-signals',
      title: 'Send billable signals',
      description: 'Send billable signals to your agents',
      completed: false,
      url: '/developers/signals',
    },
    {
      id: 'send-cost-signals',
      title: 'Send cost signals',
      description: 'Send cost signals to your agents',
      completed: false,
      url: '/developers/signals',
    },
    {
      id: 'see-roi',
      title: 'View ROI',
      description: 'See your ROI',
      completed: false,
      url: '/monetization-dashboard',
    },
  ]

  const completedCount = checklistItems.filter(item => item.completed).length
  const progressPercentage = (completedCount / checklistItems.length) * 100

  return (
    <Card className='bg-transparent border-muted/50'>
      <CardHeader>
        <CardTitle className='flex items-center gap-2'>
          <Rocket className='h-5 w-5' />
          Getting Started
        </CardTitle>
        <div className='space-y-2'>
          <div className='flex items-center justify-between text-sm'>
            <span className='text-muted-foreground'>
              {completedCount} of {checklistItems.length} completed
            </span>
            <span className='font-medium'>
              {Math.round(progressPercentage)}%
            </span>
          </div>
          <Progress value={progressPercentage} className='h-2' />
        </div>
      </CardHeader>
      <CardContent>
        <div className='space-y-3'>
          {checklistItems.map(item => (
            <div
              key={item.id}
              className='flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors'
            >
              <div className='flex-shrink-0'>
                {item.completed ? (
                  <CheckCircle className='h-5 w-5 text-green-500' />
                ) : (
                  <Circle className='h-5 w-5 text-muted-foreground' />
                )}
              </div>
              <div className='flex-1 min-w-0'>
                <div
                  className={`font-medium text-sm ${item.completed ? 'line-through text-muted-foreground' : ''}`}
                >
                  {item.title}
                </div>
                <div className='text-xs text-muted-foreground'>
                  {item.description}
                </div>
              </div>
              {!item.completed && item.url && (
                <Link to={item.url}>
                  <Button size='sm' variant='ghost' className='flex-shrink-0'>
                    <ChevronRight className='h-4 w-4' />
                  </Button>
                </Link>
              )}
            </div>
          ))}
        </div>

        {completedCount === checklistItems.length && (
          <div className='mt-4 p-4 bg-green-50 border border-green-200 rounded-lg'>
            <div className='flex items-center gap-2 text-green-800'>
              <CheckCircle className='h-5 w-5' />
              <div className='font-medium'>Setup Complete!</div>
            </div>
            <div className='text-sm text-green-700 mt-1'>
              You're all set to start monetizing your agents. Check out the{' '}
              <Link
                to='/monetization-dashboard'
                className='underline font-medium'
              >
                Monetization Dashboard
              </Link>{' '}
              to monitor your progress.
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
